import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Magnetic } from '../../ui/Magnetic/Magnetic';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../../assets/logo.jpeg';
import './Navbar.css';

const links = [
  { label: 'Work', href: '#showcase' },
  { label: 'Categories', href: '#categories' },
  { label: 'Services', href: '#services' },
  { label: 'Studio', href: '#about' },
  { label: 'Contact', href: '/contact' },
];

const MotionLink = motion(Link);

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleBrandClick = (e: React.MouseEvent) => {
    if (pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <motion.header
        className={`nav ${scrolled ? 'nav--scrolled' : ''}`}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="nav__inner container">
          <Link to="/" className="nav__brand" onClick={handleBrandClick}>
            <img src={logo} alt="Luminar Entertainments" className="nav__logo" />
            <span className="nav__wordmark">
              <span className="nav__name">Luminar</span>
              <span className="nav__sub">Entertainments</span>
            </span>
          </Link>

          <nav className="nav__links">
            {links.map((l) => {
              const isPage = l.href.startsWith('/');
              return (
                <Magnetic key={l.href} strength={0.15}>
                  {isPage ? (
                    <Link to={l.href} className="nav__link">
                      <span>{l.label}</span>
                    </Link>
                  ) : pathname === '/' ? (
                    <a href={l.href} className="nav__link">
                      <span>{l.label}</span>
                    </a>
                  ) : (
                    <Link to={`/${l.href}`} className="nav__link">
                      <span>{l.label}</span>
                    </Link>
                  )}
                </Magnetic>
              );
            })}
          </nav>

          <div className="nav__cta">
            <Magnetic strength={0.2}>
              <Link to="/book-tickets" className="nav__pill">
                <span className="nav__pill-dot" />
                <span>Book Tickets</span>
              </Link>
            </Magnetic>

            <button
              className="nav__burger"
              onClick={() => setOpen((s) => !s)}
              aria-label="Open menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="mobile-nav"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <motion.div
              className="mobile-nav__panel"
              initial={{ y: '-100%' }}
              animate={{ y: 0 }}
              exit={{ y: '-100%' }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <nav className="mobile-nav__links">
                {/* Book Tickets — always first */}
                <MotionLink
                  to="/book-tickets"
                  onClick={() => setOpen(false)}
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                  <span className="mobile-nav__num">01</span>
                  <span className="mobile-nav__label">Book Tickets</span>
                </MotionLink>

                {links.map((l, i) => {
                  const isHome = pathname === '/';
                  const idx = i + 1; // offset numbering since Book Tickets is 01
                  const isPage = l.href.startsWith('/');

                  if (isPage) {
                    return (
                      <MotionLink
                        key={l.href}
                        to={l.href}
                        onClick={() => setOpen(false)}
                        initial={{ y: 60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 + (idx) * 0.07, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                      >
                        <span className="mobile-nav__num">0{idx + 1}</span>
                        <span className="mobile-nav__label">{l.label}</span>
                      </MotionLink>
                    );
                  }

                  return isHome ? (
                    <motion.a
                      key={l.href}
                      href={l.href}
                      onClick={() => setOpen(false)}
                      initial={{ y: 60, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 + (idx) * 0.07, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <span className="mobile-nav__num">0{idx + 1}</span>
                      <span className="mobile-nav__label">{l.label}</span>
                    </motion.a>
                  ) : (
                    <MotionLink
                      key={l.href}
                      to={`/${l.href}`}
                      onClick={() => setOpen(false)}
                      initial={{ y: 60, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 + (idx) * 0.07, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <span className="mobile-nav__num">0{idx + 1}</span>
                      <span className="mobile-nav__label">{l.label}</span>
                    </MotionLink>
                  );
                })}
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
